// src/lib/discounts.js
import { supabase } from './supabase';

// ── Anti-abuse: single active reward gate ─────────────────────────────────────
// Called by ALL reward creation paths before any insert.
// Returns { blocked: bool, reward: object|null }
// Uses a DB query — not local state — so it's race-condition safe.
export async function checkActiveRewardBlock(userId) {
  if (!userId) return { blocked: false, reward: null };

  const { data, error } = await supabase
    .from('user_rewards')
    .select('id, label, discount_code, expires_at, created_at')
    .eq('user_id', userId)
    .eq('active', true)
    .eq('redeemed', false)
    .gt('expires_at', new Date().toISOString())   // not expired
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    // On DB error, fail open — log but don't block user
    console.warn('[discounts] checkActiveRewardBlock error:', error.message);
    return { blocked: false, reward: null };
  }

  return data
    ? { blocked: true,  reward: data }
    : { blocked: false, reward: null };
}

// ── Validate a discount code ──────────────────────────────────────────────────
export async function validateDiscountCode(rawCode, cartSubtotalEur, userId) {
  const code = rawCode.trim().toUpperCase();
  if (!code) return { valid: false, message: 'Enter a discount code.' };

  const { data, error } = await supabase
    .from('discount_codes')
    .select('*')
    .eq('code', code)
    .eq('active', true)
    .maybeSingle();

  if (error || !data) return { valid: false, message: 'Code not found or no longer active.' };

  if (data.expires_at && new Date(data.expires_at) < new Date())
    return { valid: false, message: 'This code has expired.' };

  if (data.max_uses !== null && (data.current_uses ?? data.uses ?? 0) >= data.max_uses)
    return { valid: false, message: 'This code has reached its maximum usage limit.' };

  if (data.single_use && data.used_by !== null)
    return { valid: false, message: 'This code has already been used.' };

  if (data.source === 'wheel' && data.owner_user_id && data.owner_user_id !== userId)
    return { valid: false, message: 'This code belongs to a different account.' };

  if (data.min_order_eur > 0 && cartSubtotalEur < data.min_order_eur)
    return { valid: false, message: `Minimum order €${Number(data.min_order_eur).toFixed(2)} required.` };

  return buildResult(data, cartSubtotalEur);
}

// ── Auto-fetch best reward for a user ────────────────────────────────────────
export async function fetchBestUserReward(userId, cartSubtotalEur) {
  if (!userId) return null;

  const { data: rewards } = await supabase
    .from('user_rewards')
    .select('*')
    .eq('user_id', userId)
    .eq('active', true)
    .eq('redeemed', false)
    .order('created_at', { ascending: false });

  if (!rewards?.length) return null;

  const codes = rewards.map(r => r.discount_code).filter(Boolean);
  if (!codes.length) return null;

  const { data: dcs } = await supabase
    .from('discount_codes')
    .select('*')
    .in('code', codes)
    .eq('active', true);

  if (!dcs?.length) return null;

  const dcMap = Object.fromEntries(dcs.map(d => [d.code, d]));

  const valid = [];
  for (const r of rewards) {
    const dc = dcMap[r.discount_code];
    if (!dc) continue;
    if (dc.used_by) continue;
    if (dc.expires_at && new Date(dc.expires_at) < new Date()) continue;
    if (dc.min_order_eur > 0 && cartSubtotalEur < dc.min_order_eur) continue;
    const result = buildResult(dc, cartSubtotalEur);
    if (result.valid) valid.push({ ...result, userRewardId: r.id, rewardLabel: r.label });
  }

  if (!valid.length) return null;
  valid.sort((a, b) => b.discountEur - a.discountEur);
  return valid[0];
}

// ── Fetch all user rewards ────────────────────────────────────────────────────
export async function fetchUserRewards(userId) {
  if (!userId) return [];
  const { data } = await supabase
    .from('user_rewards')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  return data || [];
}

// ── Create spin reward — guarded by checkActiveRewardBlock ────────────────────
// source: 'wheel' | 'signup' | 'order' | 'admin'
// Returns: { code: string } on success
// Throws:  { blocked: true, reward: object } if user already has active reward
export async function createSpinReward(userId, rewardType, rewardValue, rewardLabel, source = 'wheel') {
  // ── Anti-abuse gate (authoritative DB check) ──────────────────────────────
  // 'spins' rewards don't create discount codes — they add to spin_balance.
  // Only gate discount-generating rewards (percent, fixed, free_shipping).
  if (rewardType !== 'spins') {
    const { blocked, reward } = await checkActiveRewardBlock(userId);
    if (blocked) {
      const err = new Error('ACTIVE_REWARD_EXISTS');
      err.blocked = true;
      err.reward  = reward;
      throw err;
    }
  }

  const code      = generateSpinCode();
  const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

  const { data: dc, error: dcErr } = await supabase
    .from('discount_codes')
    .insert({
      code,
      type:          rewardType === 'free_shipping' ? 'free_shipping' : 'percent',
      value:         rewardType === 'free_shipping' ? 0 : rewardValue,
      source:        'wheel',
      single_use:    true,
      owner_user_id: userId,
      active:        true,
      expires_at:    expiresAt,
    })
    .select()
    .single();

  if (dcErr) throw new Error('Code creation failed: ' + dcErr.message);

  const { error: urErr } = await supabase.from('user_rewards').insert({
    user_id:       userId,
    discount_code: code,
    reward_type:   rewardType,
    reward_value:  rewardValue,
    label:         rewardLabel,
    source,
    active:        true,
    redeemed:      false,
    expires_at:    expiresAt,
  });

  if (urErr) {
    // Roll back the discount_code insert to keep data consistent
    await supabase.from('discount_codes').delete().eq('code', code);
    throw new Error('Reward record failed: ' + urErr.message);
  }

  return code;
}

// ── Mark a reward as redeemed ────────────────────────────────────────────────
export async function markRewardRedeemed(code, userId) {
  const now = new Date().toISOString();
  await Promise.all([
    supabase.from('discount_codes')
      .update({ used_by: userId, used_at: now })
      .eq('code', code),
    supabase.from('user_rewards')
      .update({ redeemed: true, redeemed_at: now, active: false })
      .eq('discount_code', code)
      .eq('user_id', userId),
  ]);
}

// ── Internal helper ───────────────────────────────────────────────────────────
function buildResult(data, cartSubtotalEur) {
  let discountEur  = 0;
  let displayLabel = '';

  if (data.type === 'percent') {
    discountEur  = parseFloat(((cartSubtotalEur * Number(data.value)) / 100).toFixed(2));
    displayLabel = `${data.value}% off`;
  } else if (data.type === 'fixed') {
    discountEur  = Math.min(Number(data.value), cartSubtotalEur);
    displayLabel = `€${Number(data.value).toFixed(2)} off`;
  } else if (data.type === 'free_shipping') {
    discountEur  = 0;
    displayLabel = 'Free shipping';
  }

  return {
    valid: true,
    code: data.code,
    type: data.type,
    value: data.value,
    discountEur,
    displayLabel,
    isFreeShipping: data.type === 'free_shipping',
    message: `✓ ${displayLabel} applied!`,
    dbRecord: data,
  };
}

export function generateSpinCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'SPIN-';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}